import cn from 'classnames';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { setSlotFolderPickAction, updateSlotFileSetAction } from 'editor/src/store/app/slice';
import { SlotFileSet, SlotFormat } from 'editor/src/store/design/types';
import getSlotFileSetOptionIndexes from 'editor/src/store/design/util/slotLayout/getSlotFileSetOptionIndexes';
import getSlotFolderAssets from 'editor/src/store/design/util/slotLayout/getSlotFolderAssets';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';
import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';
import Checkbox from 'editor/src/component/Checkbox';
import IconBin from 'editor/src/component/Icon/IconBin';
import Input from 'editor/src/component/Input';
import TextInput from 'editor/src/component/TextInput';

import styles from './index.module.scss';

interface Props {
  fileSets: SlotFileSet[];
  formats: SlotFormat[];
  productOptions: string[];
  onChange: (fileSets: SlotFileSet[]) => void;
  /** The sets are wired into layers already: only a folder a format lacks, or one that has
   *  nothing to show, can change. */
  disabled?: boolean;
}

/**
 * The pictures each slot offers: one set per kind of picture (the product itself, an advert),
 * with a folder per slot format and, when the layout has a per-product choice, the options
 * the picture shows under.
 */
function FileSetsSection({ fileSets, formats, productOptions, onChange, disabled }: Props) {
  const tr = useTr();
  const dispatch = useDispatch();
  const folders = useSelector((state) => state.collections.folders);

  // Per-set merges in the store: the name input reports its change after a debounce, and a
  // folder from the host's library can land in between.
  const update = (id: string, patch: Partial<SlotFileSet>) => dispatch(updateSlotFileSetAction({ id, patch }));

  const chooseFolder = (fileSetId: string, columns: SlotFormat) => {
    dispatch(setSlotFolderPickAction({ fileSetId, columns }));
    sendPostMessage('advancedPersonalisation.openLibrary', undefined);
  };

  const shownFor = (fileSet: SlotFileSet): string[] =>
    (getSlotFileSetOptionIndexes(fileSet, productOptions) ?? productOptions.map((_, i) => i)).map(
      (i) => productOptions[i],
    );
  const toggleOption = (fileSet: SlotFileSet, label: string, on: boolean) => {
    const current = shownFor(fileSet);
    const next = on
      ? productOptions.filter((o) => o === label || current.includes(o))
      : current.filter((o) => o !== label);
    // Shown under no option would mean shown always, with no option to pick it by.
    if (next.length) {
      update(fileSet.id, { options: next.length === productOptions.length ? undefined : next });
    }
  };

  return (
    <div className={styles.fileSets}>
      {fileSets.map((fileSet) => (
        <div key={fileSet.id} className={styles.fileSet}>
          <div className={styles.fileSetHeader}>
            {disabled ? (
              <Input type="text" className={styles.fileSetName} value={fileSet.name} disabled />
            ) : (
              <TextInput
                className={styles.fileSetName}
                value={fileSet.name}
                onChange={(name) => update(fileSet.id, { name })}
                placeholder={tr('editor-slot-layout-file-set-name', 'Picture set name')}
              />
            )}
            {fileSets.length > 1 && !disabled && (
              <Button
                transparent
                noPadding
                className={styles.removeFileSet}
                ariaLabel={tr('editor-slot-layout-remove-file-set', 'Remove picture set')}
                onClick={() => onChange(fileSets.filter((item) => item.id !== fileSet.id))}
              >
                <IconBin />
              </Button>
            )}
          </div>
          {formats.map((columns) => {
            const folderRef = fileSet.folders[columns];
            const folder = folderRef ? folders[folderRef.folderId] : undefined;
            const assets = folderRef ? getSlotFolderAssets(folder, folderRef) : [];
            // A folder picked earlier arrives on demand; one with nothing in it fills no slot.
            const loading = !!folderRef && !folder;
            const empty = !!folder && assets.length === 0;
            return (
              <div key={columns} className={cn(styles.folderRow, { [styles.folderEmpty]: empty })}>
                <span className={styles.folderLabel}>
                  {tr('editor-slot-layout-format', '{{count}} per row', { count: columns })}
                </span>
                <Button
                  secondary
                  small
                  disabled={disabled && assets.length > 0}
                  className="cy-slot-layout-choose-folder"
                  onClick={() => chooseFolder(fileSet.id, columns)}
                >
                  {folder && `${folder.name} (${assets.length})`}
                  {loading && tr('editor-slot-layout-folder-loading', 'Loading folder…')}
                  {!folderRef && tr('editor-slot-layout-choose-folder', 'Choose folder…')}
                </Button>
                {empty && (
                  <span className={cn(styles.folderNote, 'cy-slot-layout-folder-empty')}>
                    {tr('editor-slot-layout-folder-empty', 'No pictures in this folder')}
                  </span>
                )}
              </div>
            );
          })}
          {productOptions.length >= 2 && (
            <div className={styles.shownFor}>
              <span className={styles.folderLabel}>{tr('editor-slot-layout-shown-for', 'Shown for')}</span>
              {productOptions.map((label, index) => (
                <Checkbox
                  key={index}
                  className={`cy-slot-layout-option-${index}`}
                  label={label}
                  disabled={disabled}
                  on={shownFor(fileSet).includes(label)}
                  onChange={(on) => toggleOption(fileSet, label, on)}
                />
              ))}
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <Button
          secondary
          small
          className="cy-slot-layout-add-file-set"
          onClick={() =>
            onChange([
              ...fileSets,
              { id: uuidv4(), name: tr('editor-slot-layout-default-pictures', 'Pictures'), folders: {} },
            ])
          }
        >
          {tr('editor-slot-layout-add-file-set', 'Add picture set')}
        </Button>
      )}
    </div>
  );
}

export default React.memo(FileSetsSection);
